import { produce } from "immer";
import { defineStore } from "pinia";

import { getServiceAttributes } from "@/helpers/common";
import {
  getNestedFormula,
  getServiceProviderMap,
} from "@/helpers/customAttributes";
import attributeBuilderService from "@/services/modules/attributeBuilder";
import vuexStore from "@/store";

import type { IResponseMeta, ParamRecord } from "@/models/common";
import type {
  CustomAttribute,
  CustomAttributeBuilderState,
  CustomAttributeFormulaItem,
} from "@/models/customAttributes/customAttribute";
import type {
  DataServiceInfo,
  IAttribute,
  IContextSetValueModel,
} from "@/models/orchestration";
import type { IStepAttribute } from "@/models/orchestration";

const getDefaultState = (): CustomAttributeBuilderState => ({
  services: [],
  gettingServices: false,
  attributes: [],
  attributesMeta: {} as IResponseMeta,
  templateAttributes: [],
  templateAttributesMeta: {} as IResponseMeta,
  gettingAttributes: false,
  gettingTemplateAttributes: false,
  currentAttribute: null,
  activeItemLevel: [],
  serviceDetailsMap: {} as Record<string, DataServiceInfo>,
});

export const useAttributeBuilderStore = defineStore("attributeBuilder", {
  state: getDefaultState,
  getters: {
    attributeStatusOptions() {
      return vuexStore.state.options.all?.attribute?.statuses || [];
    },
    activeItemLevelString(): string {
      return this.activeItemLevel ? this.activeItemLevel.join() : "";
    },
    serviceAttributes(): IAttribute[] {
      return this.services.reduce<IAttribute[]>(getServiceAttributes, []);
    },
  },
  actions: {
    async getServices(params: ParamRecord = {}) {
      if (this.gettingServices) {
        return;
      }
      try {
        this.gettingServices = true;
        const services = await attributeBuilderService.getServices(params);
        this.services = produce(services, (draftState) => {
          draftState.forEach((service) => {
            service.attributes?.forEach((attr) => {
              if (!attr.context?.id) {
                return;
              }
              attr.context = {
                value: { id: attr.context.id },
                type: attr.context.type,
              };
            });
          });
        });
        this.serviceDetailsMap = await getServiceProviderMap(this.services);
      } finally {
        this.gettingServices = false;
      }
    },
    async getAttributes(params: ParamRecord = {}) {
      const forTemplate =
        params.exclude_active !== undefined && !params.exclude_active;
      if (
        (forTemplate && this.gettingTemplateAttributes) ||
        (!forTemplate && this.gettingAttributes)
      ) {
        return;
      }
      try {
        forTemplate
          ? (this.gettingTemplateAttributes = true)
          : (this.gettingAttributes = true);
        const { data, meta } =
          await attributeBuilderService.getAttributes(params);
        const key = forTemplate ? "templateAttributes" : "attributes";
        this[key] = data.map((attribute) => ({
          ...attribute,
          statusText: this.attributeStatusOptions[attribute.status],
        }));
        const metaKey = forTemplate
          ? "templateAttributesMeta"
          : "attributesMeta";
        this[metaKey] = meta;
      } finally {
        forTemplate
          ? (this.gettingTemplateAttributes = false)
          : (this.gettingAttributes = false);
      }
    },

    async getAttribute(id: string) {
      const attribute = await attributeBuilderService.getAttribute(id);
      this.currentAttribute = attribute;
      return attribute;
    },

    async deleteAttribute(id: string) {
      await attributeBuilderService.deleteAttribute(id);
    },

    async createAttribute(payload: Partial<CustomAttribute>) {
      return await attributeBuilderService.createAttribute(payload);
    },

    async updateAttribute(payload: Partial<CustomAttribute>) {
      const result = await attributeBuilderService.updateAttribute(payload);
      if (result) {
        this.currentAttribute = result;
      }
      return result;
    },

    setActiveLevel(level: number[]) {
      this.activeItemLevel = level;
    },

    prependFormulaItem(
      index: number,
      item: CustomAttributeFormulaItem | CustomAttributeFormulaItem[],
      level?: number[]
    ) {
      if (!this.currentAttribute?.content?.formula) {
        return;
      }
      if (!level) {
        this.currentAttribute.content.formula.splice(index, 0, item);
        return;
      }
      const nestedFormula = getNestedFormula(
        level,
        this.currentAttribute.content.formula
      );
      nestedFormula.splice(index, 0, item);
      this.activeItemLevel = [];
    },

    appendFormulaItem(
      item: CustomAttributeFormulaItem | CustomAttributeFormulaItem[],
      level?: number[]
    ) {
      if (!this.currentAttribute?.content?.formula) {
        return;
      }
      const formula = level
        ? getNestedFormula(level, this.currentAttribute.content.formula)
        : this.currentAttribute.content.formula;

      this.prependFormulaItem(formula.length, item, level);
    },

    removeFormulaItem(level: number[], index: number) {
      if (!this.currentAttribute?.content?.formula) {
        return;
      }
      const nestedFormula = getNestedFormula(
        level,
        this.currentAttribute.content.formula
      );
      nestedFormula.splice(index, 1);
      this.activeItemLevel = [];
    },

    setContext(item: IStepAttribute, context: IContextSetValueModel) {
      item.context = context;
      this.activeItemLevel = [];
    },

    unsetTemplateAttributesAndMeta() {
      this.templateAttributes = getDefaultState().templateAttributes;
      this.templateAttributesMeta = getDefaultState().templateAttributesMeta;
    },
  },
});
